import { type MutationOptions, useMutation, useQueryClient } from '@tanstack/react-query';
import type { AxiosInstance, AxiosResponse } from 'axios';
import type { InvoiceFormFieldValues } from 'modules/Invoices/InvoiceForm/InvoiceForm.types';
import { invoiceFormToData } from 'shared/serializer';
import { useGetConstants } from 'shared/utils/selectors';
import type { StoreState } from 'types/storeTypes';
import { CreditNoteUpdateParamsSchema, type InvoiceResponse } from './invoice-schemas';
import { type ApiError, addActionNameToMutationResult, useLegacyAxiosInstance } from './utils';

type UpdateCreditNoteVariables = {
  id: number;
  data: InvoiceFormFieldValues;
};

export async function updateCreditNoteFn(
  axiosInstance: Promise<AxiosInstance>,
  variables: UpdateCreditNoteVariables,
  constants: StoreState['app']['constants'],
) {
  const instance = await axiosInstance;
  const serialized = invoiceFormToData(variables.data, constants);
  const validated = CreditNoteUpdateParamsSchema.parse(serialized);
  return instance.put<InvoiceResponse>(`/credit_notes/${variables.id}`, validated);
}

export function useUpdateCreditNote(
  options: MutationOptions<
    AxiosResponse<InvoiceResponse>,
    ApiError,
    UpdateCreditNoteVariables
  > = {},
) {
  const axiosInstance = useLegacyAxiosInstance();
  const constants = useGetConstants();
  const queryClient = useQueryClient();

  const mutation = useMutation<AxiosResponse<InvoiceResponse>, ApiError, UpdateCreditNoteVariables>(
    {
      mutationFn: variables => updateCreditNoteFn(axiosInstance, variables, constants),
      onSuccess: (_response, variables) => {
        queryClient.invalidateQueries({ queryKey: ['credit-notes', variables.id] });
      },
      ...options,
    },
  );

  return addActionNameToMutationResult<
    AxiosResponse<InvoiceResponse>,
    ApiError,
    UpdateCreditNoteVariables,
    'updateCreditNote'
  >('updateCreditNote', mutation);
}
